/* KOJO custom layer — core runtime (cart, drawers, events, quick add, utilities).
   Independent from the Blum vendor code: only kojo-* files depend on it. */
(function () {
  'use strict';
  if (window.KJ && window.KJ.__core) return;

  var doc = document;
  var root = doc.documentElement;

  /* ---------- config ---------- */
  function readConfig() {
    var el = doc.getElementById('kj-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
  }
  var config = readConfig();
  config.routes = config.routes || {};
  var routes = {
    root: config.routes.root || '/',
    cart: config.routes.cart || '/cart',
    cartAdd: config.routes.cartAdd || '/cart/add',
    cartChange: config.routes.cartChange || '/cart/change',
    cartUpdate: config.routes.cartUpdate || '/cart/update',
    search: config.routes.search || '/search',
    predictive: config.routes.predictive || '/search/suggest',
    recommendations: config.routes.recommendations || '/recommendations/products'
  };
  function t(key, fallback) {
    return (config.strings && config.strings[key]) || fallback || key;
  }

  /* ---------- helpers ---------- */
  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function emit(name, detail, target) {
    (target || doc).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }
  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments, self = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(self, args); }, wait || 150);
    };
  }
  function url(path) {
    var r = routes.root || '/';
    if (r !== '/' && path.indexOf(r) !== 0 && path.charAt(0) === '/') return r.replace(/\/$/, '') + path;
    return path;
  }
  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- money ---------- */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;
    if (isNaN(cents) || cents === null) return '0';
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsStr = parts[1] ? decimal + parts[1] : '';
    return dollars + centsStr;
  }
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    if (!format && config.priceStyle === 'suffix') {
      var v = formatWithDelimiters(cents, 2, '.', ',').replace(/,00$/, '');
      return v + ' ' + (config.currencySymbol || '€');
    }
    format = format || config.moneyFormat || '€{{amount_with_comma_separator}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    if (config.trimZeroDecimals) value = value.replace(/[.,]00$/, '');
    return format.replace(placeholder, value);
  }

  /* ---------- scroll lock (ref-counted) ---------- */
  var locks = new Set();
  var lockedY = 0;
  function lockScroll(owner) {
    if (!locks.size) {
      lockedY = window.pageYOffset || 0;
      var sbw = window.innerWidth - root.clientWidth;
      root.style.setProperty('--kj-scrollbar', sbw + 'px');
      root.classList.add('kj-locked');
    }
    locks.add(owner || 'default');
  }
  function unlockScroll(owner) {
    locks.delete(owner || 'default');
    if (!locks.size) root.classList.remove('kj-locked');
  }

  /* ---------- focus trap ----------
     Two layers: (1) Tab / Shift+Tab on the first/last focusable wraps around — shadow hosts (Shop Pay / Google Pay
     wallet buttons) count as focusable, since querySelectorAll cannot see inside them; (2) a document focusin guard
     that pulls focus back when a Tab press still lets it escape the container (e.g. out of a shadow root). The guard
     only reacts to keyboard Tab moves coming from inside the container, so third-party modals (Shop Pay login…)
     that take focus programmatically are left alone. */
  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),iframe,summary';
  var lastTab = { on: false, shift: false, t: 0 };
  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') { lastTab.on = true; lastTab.shift = e.shiftKey; lastTab.t = Date.now(); } else { lastTab.on = false; }
  }, true);
  doc.addEventListener('pointerdown', function () { lastTab.on = false; }, true);
  function isVisible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
  // rendered, not visibility:hidden and not parked off-screen (wallet SDKs keep hidden pre-render iframes at x > viewport)
  function isTabbable(el) {
    if (!isVisible(el)) return false;
    if (window.getComputedStyle(el).visibility === 'hidden') return false;
    var r = el.getBoundingClientRect();
    return r.right > 0 && r.left < (window.innerWidth || root.clientWidth);
  }
  function focusables(container) {
    var out = [];
    var all = container.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if ((el.shadowRoot || el.matches(FOCUSABLE)) && isTabbable(el)) out.push(el);
    }
    return out;
  }
  // focus inside a shadow root reports the host as document.activeElement, so strict equality covers wallet buttons
  function hasFocus(el) { return !!el && doc.activeElement === el; }
  function tryFocus(el, noScroll) {
    if (!el) return false;
    try { el.focus({ preventScroll: !!noScroll }); } catch (e) {}
    if (hasFocus(el)) return true;
    if (el.shadowRoot) {
      var inner = el.shadowRoot.querySelector(FOCUSABLE);
      if (inner) { try { inner.focus({ preventScroll: !!noScroll }); } catch (e) {} }
      return hasFocus(el);
    }
    return false;
  }
  // focus the first (dir 1) or last (dir -1) element of the list that really accepts focus
  function focusEdge(items, dir, fallback) {
    for (var i = dir > 0 ? 0 : items.length - 1; i >= 0 && i < items.length; i += dir) {
      if (tryFocus(items[i])) return true;
    }
    if (fallback) tryFocus(fallback);
    return false;
  }
  function trapFocus(container) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      var items = focusables(container);
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && hasFocus(first)) { e.preventDefault(); focusEdge(items, -1, container); }
      else if (!e.shiftKey && hasFocus(last)) { e.preventDefault(); focusEdge(items, 1, container); }
    }
    function guard(e) {
      var tgt = e.target;
      if (!tgt || tgt === doc || tgt === window || container.contains(tgt)) return;
      if (!lastTab.on || Date.now() - lastTab.t > 1500) return;
      var from = e.relatedTarget;
      if (from && !container.contains(from)) return; // moving between elements outside: not our escape
      var modal = tgt.closest && tgt.closest('[aria-modal="true"]');
      if (modal && !modal.contains(container)) return; // another dialog took focus on purpose
      if (!isVisible(container)) return;
      focusEdge(focusables(container), lastTab.shift ? -1 : 1, container);
      // the escaped focus may have scrolled the locked page behind the overlay: put it back
      if (locks.size && Math.abs((window.pageYOffset || 0) - lockedY) > 1) window.scrollTo(window.pageXOffset || 0, lockedY);
    }
    container.addEventListener('keydown', handler);
    doc.addEventListener('focusin', guard, true);
    return function release() {
      container.removeEventListener('keydown', handler);
      doc.removeEventListener('focusin', guard, true);
    };
  }
  // return focus to the element that opened an overlay; if it is gone or hidden (e.g. a size chip inside a
  // collapsed quick-add panel), focus the nearest visible focusable around it instead of dropping to <body>
  function restoreFocus(opener) {
    if (!opener || !opener.focus || !doc.contains(opener) || opener === doc.body) return;
    if (isVisible(opener) && tryFocus(opener, true)) return;
    var node = opener.parentElement, hops = 0;
    while (node && node !== doc.body && hops < 6) {
      var cand = qsa(FOCUSABLE, node).filter(isVisible);
      for (var i = 0; i < cand.length; i++) { if (tryFocus(cand[i], true)) return; }
      node = node.parentElement; hops++;
    }
  }

  /* ---------- generic drawer / overlay element ----------
     <kj-drawer id="..." data-name="menu" aria-hidden="true">
       <div class="kj-drawer__overlay" data-kj-close></div>
       <div class="kj-drawer__panel" role="dialog" aria-modal="true" tabindex="-1"> ... [data-kj-close] ... </div>
     </kj-drawer>
     Open with KJ.drawers.open('name') or any [data-kj-open="name"] trigger. */
  var openStack = [];
  var KJDrawerBase = function (Base) {
    return class extends Base {
      connectedCallback() {
        if (this._kjReady) return;
        this._kjReady = true;
        this.setAttribute('aria-hidden', this.classList.contains('is-open') ? 'false' : 'true');
        this.addEventListener('click', (e) => {
          var closer = e.target.closest('[data-kj-close]');
          if (closer && this.contains(closer)) { e.preventDefault(); this.close(); }
        });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { e.stopPropagation(); this.close(); }
        });
      }
      get name() { return this.dataset.name || this.id; }
      get isOpen() { return this.classList.contains('is-open'); }
      open(opener) {
        if (this.isOpen) return;
        this._opener = opener || doc.activeElement;
        this.removeAttribute('hidden');
        // force reflow so the transition runs
        void this.offsetWidth;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        lockScroll(this.name);
        var panel = this.querySelector('[role="dialog"]') || this;
        this._release = trapFocus(panel);
        var focusTarget = this.querySelector('[data-kj-autofocus]') || panel;
        setTimeout(function () { try { focusTarget.focus({ preventScroll: true }); } catch (e) {} }, 60);
        qsa('[aria-controls="' + this.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        openStack.push(this);
        emit('kj:drawer:open', { name: this.name, el: this });
      }
      close() {
        if (!this.isOpen) return;
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        unlockScroll(this.name);
        if (this._release) this._release();
        qsa('[aria-controls="' + this.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        var idx = openStack.indexOf(this);
        if (idx > -1) openStack.splice(idx, 1);
        restoreFocus(this._opener);
        this._opener = null;
        emit('kj:drawer:close', { name: this.name, el: this });
      }
      toggle(opener) { this.isOpen ? this.close() : this.open(opener); }
    };
  };
  if (!customElements.get('kj-drawer')) customElements.define('kj-drawer', KJDrawerBase(HTMLElement));

  var drawers = {
    get: function (name) {
      return qs('kj-drawer[data-name="' + name + '"]') || qs('kj-cart-drawer[data-name="' + name + '"]') || doc.getElementById(name);
    },
    open: function (name, opener) { var d = drawers.get(name); if (d && d.open) { d.open(opener); return true; } return false; },
    close: function (name) { var d = drawers.get(name); if (d && d.close) d.close(); },
    closeAll: function () { openStack.slice().forEach(function (d) { d.close(); }); }
  };

  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-kj-open]');
    if (!trigger) return;
    var name = trigger.getAttribute('data-kj-open');
    if (drawers.open(name, trigger)) e.preventDefault();
  });

  /* ---------- cart API ---------- */
  var state = { cart: null, busy: false };

  function cartDrawerEl() { return qs('kj-cart-drawer'); }
  function sectionIds() {
    var ids = [];
    var d = cartDrawerEl();
    if (d && d.dataset.sectionId) ids.push(d.dataset.sectionId);
    qsa('[data-kj-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-kj-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function request(path, body) {
    return fetch(url(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Kojo': '1' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok || json.status === 422 || json.status === 'bad_request') {
          var err = new Error(json.description || json.message || t('cart_error', 'No se ha podido actualizar el carrito'));
          err.data = json;
          throw err;
        }
        return json;
      });
    });
  }

  function fetchCart() {
    return fetch(url('/cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Kojo': '1' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; updateCount(cart.item_count); return cart; });
  }

  function updateCount(count) {
    qsa('[data-kj-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.setAttribute('data-count', count);
      el.hidden = !count;
    });
    qsa('[data-kj-cart-count-label]').forEach(function (el) {
      el.textContent = String(count);
    });
  }

  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (!html) return;
      var d = cartDrawerEl();
      if (d && d.dataset.sectionId === id) { d.renderFromHTML(html); return; }
      qsa('[data-kj-cart-section="' + id + '"]').forEach(function (el) {
        var fresh = parseHTML(html).querySelector('[data-kj-cart-section="' + id + '"]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
    });
  }

  function add(items, opts) {
    opts = opts || {};
    // remember who triggered the add NOW: callers usually disable (setLoading) the button right after, which
    // drops focus to <body>; the drawer returns focus to this element when it closes (opts.opener wins)
    var active = doc.activeElement;
    var opener = opts.opener || (active && active !== doc.body ? active : null);
    if (!Array.isArray(items)) items = [items];
    items = items.map(function (it) {
      var o = { id: Number(it.id), quantity: Number(it.quantity || 1) };
      if (it.properties) o.properties = it.properties;
      if (it.selling_plan) o.selling_plan = it.selling_plan;
      return o;
    });
    var ids = sectionIds();
    var body = { items: items };
    if (ids.length) { body.sections = ids; body.sections_url = window.location.pathname; }
    state.busy = true;
    return request('/cart/add.js', body)
      .then(function (res) {
        renderSections(res.sections);
        return fetchCart().then(function (cart) {
          emit('kj:cart:added', { items: res.items || items, cart: cart, source: opts.source });
          emit('kj:cart:updated', { cart: cart, source: opts.source || 'add' });
          if (opts.open !== false) openCart(opener);
          return cart;
        });
      })
      .catch(function (err) {
        emit('kj:cart:error', { message: err.message, source: opts.source, data: err.data });
        throw err;
      })
      .finally(function () { state.busy = false; });
  }

  function change(line, quantity, opts) {
    opts = opts || {};
    var ids = sectionIds();
    var body = { quantity: Number(quantity) };
    if (typeof line === 'string' && line.indexOf(':') > -1) body.id = line; else body.line = Number(line);
    if (ids.length) { body.sections = ids; body.sections_url = window.location.pathname; }
    state.busy = true;
    return request('/cart/change.js', body)
      .then(function (cart) {
        state.cart = cart;
        renderSections(cart.sections);
        updateCount(cart.item_count);
        emit('kj:cart:updated', { cart: cart, source: opts.source || 'change' });
        return cart;
      })
      .catch(function (err) {
        emit('kj:cart:error', { message: err.message, source: opts.source || 'change', data: err.data });
        throw err;
      })
      .finally(function () { state.busy = false; });
  }

  function refresh() {
    var ids = sectionIds();
    if (!ids.length) return fetchCart();
    return fetch(url(window.location.pathname + (window.location.pathname.indexOf('?') > -1 ? '&' : '?') + 'sections=' + ids.join(',')), { credentials: 'same-origin', headers: { 'X-Kojo': '1' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) { renderSections(sections); return fetchCart(); })
      .then(function (cart) { emit('kj:cart:updated', { cart: cart, source: 'refresh' }); return cart; });
  }

  function openCart(opener) {
    var d = cartDrawerEl();
    if (d && d.open) { d.open(opener); return true; }
    window.location.href = url(routes.cart);
    return false;
  }
  function closeCart() { var d = cartDrawerEl(); if (d && d.close) d.close(); }

  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-kj-open-cart]');
    if (!trigger) return;
    if (cartDrawerEl()) { e.preventDefault(); openCart(trigger); }
  });

  /* ---------- buttons: loading state ---------- */
  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) btn.setAttribute('disabled', ''); else if (!btn.hasAttribute('data-kj-keep-disabled')) btn.removeAttribute('disabled');
  }

  /* ---------- toast (errors / confirmations) ---------- */
  var toastTimer;
  function toast(message, type) {
    var el = doc.getElementById('kj-toast');
    if (!el) {
      el = doc.createElement('div');
      el.id = 'kj-toast';
      el.className = 'kj-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      doc.body.appendChild(el);
    }
    el.textContent = message;
    el.dataset.type = type || 'info';
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 3800);
  }
  doc.addEventListener('kj:cart:error', function (e) {
    if (e.detail && e.detail.silent) return;
    toast(e.detail && e.detail.message ? e.detail.message : t('cart_error'), 'error');
  });

  /* ---------- <kj-cart-drawer> ---------- */
  if (!customElements.get('kj-cart-drawer')) {
    customElements.define('kj-cart-drawer', class extends KJDrawerBase(HTMLElement) {
      connectedCallback() {
        super.connectedCallback();
        if (this._cartReady) return;
        this._cartReady = true;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
      }
      renderFromHTML(html) {
        var fresh = parseHTML(html).querySelector('kj-cart-drawer');
        var target = this.querySelector('[data-kj-cart-body]');
        var source = fresh && fresh.querySelector('[data-kj-cart-body]');
        if (target && source) {
          var scroller = this.querySelector('[data-kj-cart-scroll]');
          var top = scroller ? scroller.scrollTop : 0;
          target.innerHTML = source.innerHTML;
          var scroller2 = this.querySelector('[data-kj-cart-scroll]');
          if (scroller2) scroller2.scrollTop = top;
        }
        if (fresh) {
          var count = fresh.getAttribute('data-item-count');
          if (count !== null) { this.setAttribute('data-item-count', count); updateCount(parseInt(count, 10) || 0); }
        }
        emit('kj:cart:rendered', { el: this });
      }
      onClick(e) {
        var btn = e.target.closest('[data-kj-qty]');
        if (btn && this.contains(btn)) {
          e.preventDefault();
          var line = btn.closest('[data-line]');
          if (!line) return;
          var input = line.querySelector('input[data-kj-qty-input]');
          var current = parseInt(input ? input.value : line.dataset.quantity, 10) || 0;
          var next = btn.dataset.kjQty === 'plus' ? current + 1 : btn.dataset.kjQty === 'minus' ? current - 1 : 0;
          this.changeLine(line, Math.max(0, next));
          return;
        }
        var addBtn = e.target.closest('[data-kj-add]');
        if (addBtn && this.contains(addBtn)) {
          e.preventDefault();
          var holder = addBtn.closest('[data-kj-upsell]');
          var sel = holder && holder.querySelector('select[data-kj-variant-select]');
          var vid = sel ? sel.value : addBtn.dataset.kjAdd;
          if (!vid) return;
          setLoading(addBtn, true);
          add([{ id: vid, quantity: 1 }], { source: 'cart-upsell', open: true })
            .catch(function () {})
            .finally(function () { setLoading(addBtn, false); });
        }
      }
      onChange(e) {
        var input = e.target.closest('input[data-kj-qty-input]');
        if (!input) return;
        var line = input.closest('[data-line]');
        this.changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
      }
      changeLine(lineEl, qty) {
        if (!lineEl) return;
        lineEl.classList.add('is-updating');
        change(lineEl.dataset.key || lineEl.dataset.line, qty, { source: 'cart-drawer' })
          .catch(function (err) {
            lineEl.classList.remove('is-updating');
            var errEl = lineEl.querySelector('[data-kj-line-error]');
            if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
          });
      }
    });
  }

  /* ---------- <kj-product-form> : wraps a Shopify product form ----------
     <kj-product-form data-source="pdp"><form action="/cart/add" method="post"> ... <input name="id"> ... <button type="submit" data-kj-submit> */
  if (!customElements.get('kj-product-form')) {
    customElements.define('kj-product-form', class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      onSubmit(e) {
        e.preventDefault();
        if (this.dataset.requireVariant === 'true' && !this.form.querySelector('[name="id"]').value) {
          emit('kj:variant:required', { form: this });
          return;
        }
        var btn = this.querySelector('[data-kj-submit]') || this.form.querySelector('[type="submit"]');
        var fd = new FormData(this.form);
        var id = fd.get('id');
        if (!id) { emit('kj:variant:required', { form: this }); return; }
        var props = {};
        fd.forEach(function (v, k) {
          var m = k.match(/^properties\[(.+)\]$/);
          if (m && v !== '') props[m[1]] = v;
        });
        var item = { id: id, quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
        if (Object.keys(props).length) item.properties = props;
        if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
        var extra = [];
        qsa('input[data-kj-bundle]:checked', this).forEach(function (cb) { if (cb.value) extra.push({ id: cb.value, quantity: 1 }); });
        var opener = btn || doc.activeElement;
        setLoading(btn, true);
        var self = this;
        add([item].concat(extra), { source: this.dataset.source || 'product-form', opener: opener })
          .then(function () { self.classList.add('is-added'); setTimeout(function () { self.classList.remove('is-added'); }, 1800); })
          .catch(function () {})
          .finally(function () { setLoading(btn, false); });
      }
    });
  }

  /* ---------- quick add (cards, upsells) ----------
     Any element with [data-kj-quick-add="<variantId>"] adds that variant.
     Chips: <button data-kj-quick-add="123" data-kj-source="card">20</button> */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-kj-quick-add]');
    if (!btn || btn.closest('kj-cart-drawer')) return;
    var id = btn.getAttribute('data-kj-quick-add');
    if (!id) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    // opener for focus return: the button itself, or — when it sits in a quick-* sheet that closes after the
    // add — the element that opened that sheet
    var sheet0 = btn.closest('kj-drawer');
    var opener = sheet0 && sheet0.dataset.name && sheet0.dataset.name.indexOf('quick-') === 0 && sheet0._opener ? sheet0._opener : btn;
    setLoading(btn, true);
    add([{ id: id, quantity: 1 }], { source: btn.getAttribute('data-kj-source') || 'quick-add', opener: opener })
      .then(function () {
        var sheet = btn.closest('kj-drawer');
        if (sheet && sheet.dataset.name && sheet.dataset.name.indexOf('quick-') === 0) sheet.close();
      })
      .catch(function () {})
      .finally(function () { setLoading(btn, false); });
  });

  /* ---------- quick-add toggle (touch: "+" opens size chips on the card) ---------- */
  doc.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-kj-qa-toggle]');
    qsa('[data-kj-qa].is-open').forEach(function (qa) {
      if (!toggle || !qa.contains(toggle)) {
        if (!e.target.closest('[data-kj-qa]') || !qa.contains(e.target)) {
          qa.classList.remove('is-open');
          var t2 = qa.querySelector('[data-kj-qa-toggle]');
          if (t2) {
            t2.setAttribute('aria-expanded', 'false');
            // a chip that just got focus back from the cart drawer is about to be hidden: keep focus on the card
            if (qa.contains(doc.activeElement) && doc.activeElement !== t2) { try { t2.focus({ preventScroll: true }); } catch (err) {} }
          }
        }
      }
    });
    if (!toggle) return;
    e.preventDefault();
    e.stopPropagation();
    var qa = toggle.closest('[data-kj-qa]');
    var open = !qa.classList.contains('is-open');
    qa.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ---------- <kj-carousel> : scroll-snap rail with arrows + progress ----------
     <kj-carousel><div data-kj-track> items </div><button data-kj-prev><button data-kj-next><span data-kj-progress><span data-kj-counter> */
  if (!customElements.get('kj-carousel')) {
    customElements.define('kj-carousel', class extends HTMLElement {
      connectedCallback() {
        if (!this._ready) {
          this._ready = true;
          this.track = this.querySelector('[data-kj-track]');
          if (!this.track) return;
          this.prev = this.querySelector('[data-kj-prev]');
          this.next = this.querySelector('[data-kj-next]');
          this.progress = this.querySelector('[data-kj-progress]');
          this.counter = this.querySelector('[data-kj-counter]');
          var self = this;
          if (this.prev) this.prev.addEventListener('click', function () { self.go(-1); });
          if (this.next) this.next.addEventListener('click', function () { self.go(1); });
          this.track.addEventListener('scroll', debounce(this.update.bind(this), 60), { passive: true });
          this._onResize = debounce(this.update.bind(this), 120);
        }
        if (!this.track) return;
        // size changes: observe the track (no global listener left behind when the drawer / editor replaces the
        // markup); window resize only as a fallback. Both are detached in disconnectedCallback.
        if ('ResizeObserver' in window) {
          if (!this._ro) this._ro = new ResizeObserver(this._onResize);
          this._ro.observe(this.track);
        } else {
          window.addEventListener('resize', this._onResize);
        }
        this.update();
      }
      disconnectedCallback() {
        if (this._ro) this._ro.disconnect();
        if (this._onResize) window.removeEventListener('resize', this._onResize);
      }
      items() { return Array.prototype.slice.call(this.track.children).filter(function (c) { return c.offsetParent !== null; }); }
      step() {
        var items = this.items();
        if (items.length < 2) return this.track.clientWidth;
        return items[1].offsetLeft - items[0].offsetLeft;
      }
      go(dir) {
        var visible = Math.max(1, Math.floor(this.track.clientWidth / this.step()));
        this.track.scrollBy({ left: dir * this.step() * visible, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
      update() {
        var max = this.track.scrollWidth - this.track.clientWidth;
        var x = Math.abs(this.track.scrollLeft);
        if (this.prev) this.prev.disabled = x <= 2;
        if (this.next) this.next.disabled = x >= max - 2;
        this.classList.toggle('is-scrollable', max > 2);
        if (this.progress) {
          var ratio = this.track.clientWidth / Math.max(this.track.scrollWidth, 1);
          this.progress.style.setProperty('--kj-progress-size', (ratio * 100).toFixed(2) + '%');
          this.progress.style.setProperty('--kj-progress-pos', (max > 0 ? (x / max) * (1 - ratio) * 100 : 0).toFixed(2) + '%');
        }
        if (this.counter) {
          var items = this.items();
          var idx = Math.round(x / Math.max(this.step(), 1));
          this.counter.textContent = Math.min(idx + 1, items.length) + ' / ' + items.length;
        }
        emit('kj:carousel:change', { el: this }, this);
      }
    });
  }

  /* ---------- autoplay videos only when visible ----------
     Lazy poster: render data-poster="…" instead of poster="…" and the poster is requested ~600px before the video
     scrolls into view (all data-poster videos of the same rail track at once, since off-screen slides of a
     horizontal rail never intersect). A plain poster attribute keeps working as before. */
  function loadPoster(v) {
    var p = v.getAttribute('data-poster');
    if (p && !v.getAttribute('poster')) v.setAttribute('poster', p);
  }
  var posterIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      var v = en.target;
      var track = v.closest('[data-kj-track]');
      (track ? qsa('video[data-kj-autoplay][data-poster]', track) : [v]).forEach(function (x) {
        loadPoster(x);
        posterIO.unobserve(x);
      });
    });
  }, { rootMargin: '600px 0px' }) : null;
  var videoIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var v = en.target;
      if (en.isIntersecting) {
        loadPoster(v);
        if (v.dataset.src && !v.src) { v.src = v.dataset.src; }
        var p = v.play && v.play();
        if (p && p.catch) p.catch(function () {});
      } else if (!v.paused) {
        v.pause();
      }
    });
  }, { rootMargin: '120px 0px', threshold: 0.15 }) : null;
  function initVideos(ctx) {
    qsa('video[data-kj-autoplay]', ctx).forEach(function (v) {
      if (v._kjObserved) return;
      v._kjObserved = true;
      v.muted = true;
      v.setAttribute('muted', '');
      if (v.hasAttribute('data-poster') && !v.getAttribute('poster')) {
        if (posterIO) posterIO.observe(v); else loadPoster(v);
      }
      if (reducedMotion()) { v.removeAttribute('autoplay'); return; }
      if (videoIO) videoIO.observe(v); else { loadPoster(v); if (v.dataset.src) v.src = v.dataset.src; v.play && v.play().catch(function () {}); }
    });
  }

  /* ---------- reveal on scroll ---------- */
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }) : null;
  function initReveal(ctx) {
    qsa('[data-kj-reveal]', ctx).forEach(function (el) {
      if (el._kjReveal) return;
      el._kjReveal = true;
      if (!revealIO || reducedMotion() || window.Shopify && window.Shopify.designMode) { el.classList.add('is-in'); return; }
      revealIO.observe(el);
    });
  }

  /* ---------- header height variable (also feeds vendor --header-height) ---------- */
  function measureHeader() {
    var h = qs('[data-kj-header]');
    if (!h) return;
    var height = Math.round(h.getBoundingClientRect().height);
    root.style.setProperty('--kj-header-h', height + 'px');
    root.style.setProperty('--header-height', height + 'px');
  }

  /* ---------- vendor compatibility shim ----------
     Blum add-to-cart flows (vendor product form, quick view, featured product) and app buttons talk to
     /cart/* directly and expect the vendor drawer. We (1) observe external cart mutations via fetch/XHR
     and refresh/open OUR drawer, (2) listen to the vendor 'cart:update' event, (3) provide facades for
     leftover inline handlers (window.ecxCartDrawer / window.cartNotificationPanel). Our own requests send
     the header X-Kojo so they are ignored here. Never define ecx-* custom elements (vendor defines them unguarded). */
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/;
  function onExternalMutation(action) {
    if (!cartDrawerEl()) return;
    refresh().then(function () { if (action === 'add') openCart(); }).catch(function () {});
  }
  function installNetworkHook() {
    if (window.__kjNetHook) return;
    window.__kjNetHook = true;
    var _fetch = window.fetch;
    if (_fetch) {
      window.fetch = function (input, init) {
        var u = typeof input === 'string' ? input : (input && input.url) || '';
        var p = _fetch.apply(this, arguments);
        var m = u.match(CART_RE);
        var h = init && init.headers;
        var internal = h && (h['X-Kojo'] || (typeof h.get === 'function' && h.get('X-Kojo')));
        if (m && !internal) {
          p.then(function (r) { if (r && r.ok) setTimeout(function () { onExternalMutation(m[1]); }, 0); }).catch(function () {});
        }
        return p;
      };
    }
    var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (XHR) {
      var _open = XHR.open, _send = XHR.send;
      XHR.open = function (method, u) { this.__kjUrl = String(u || ''); return _open.apply(this, arguments); };
      XHR.send = function () {
        var xhr = this;
        var m = xhr.__kjUrl && xhr.__kjUrl.match(CART_RE);
        if (m) xhr.addEventListener('load', function () { if (xhr.status >= 200 && xhr.status < 300) onExternalMutation(m[1]); });
        return _send.apply(this, arguments);
      };
    }
  }
  installNetworkHook();
  doc.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.source === 'kojo') return;
    onExternalMutation('update');
  });
  function installVendorFacades() {
    if (!('ecxCartDrawer' in window)) {
      window.ecxCartDrawer = {
        __kj: true,
        openDrawer: function (opener) { refresh().then(function () { openCart(opener); }); },
        closeDrawer: function () { closeCart(); },
        getSectionsToRender: function () { return []; },
        renderContents: function () { return refresh(); }
      };
    }
    if (!('cartNotificationPanel' in window)) {
      window.cartNotificationPanel = {
        __kj: true,
        open: function () { openCart(); },
        close: function () {},
        getSectionsToRender: function () { return []; },
        renderContents: function () { return refresh(); }
      };
    }
  }

  /* ---------- init ---------- */
  function init(ctx) {
    initVideos(ctx);
    initReveal(ctx);
  }

  var KJ = window.KJ = window.KJ || {};
  KJ.__core = true;
  KJ.config = config;
  KJ.routes = routes;
  KJ.t = t;
  KJ.qs = qs;
  KJ.qsa = qsa;
  KJ.emit = emit;
  KJ.debounce = debounce;
  KJ.parseHTML = parseHTML;
  KJ.formatMoney = formatMoney;
  KJ.lockScroll = lockScroll;
  KJ.unlockScroll = unlockScroll;
  KJ.trapFocus = trapFocus;
  KJ.drawers = drawers;
  KJ.DrawerBase = KJDrawerBase;
  KJ.setLoading = setLoading;
  KJ.toast = toast;
  KJ.reducedMotion = reducedMotion;
  KJ.cart = { state: state, add: add, change: change, fetch: fetchCart, refresh: refresh, open: openCart, close: closeCart, updateCount: updateCount };
  KJ.addToCart = add;
  KJ.openCart = openCart;
  KJ.closeCart = closeCart;
  KJ.init = init;
  KJ.measureHeader = measureHeader;

  function boot() {
    installVendorFacades();
    init(doc);
    measureHeader();
    if ('ResizeObserver' in window) {
      var h = qs('[data-kj-header]');
      if (h) new ResizeObserver(debounce(measureHeader, 50)).observe(h);
    }
    window.addEventListener('resize', debounce(measureHeader, 100));
    root.classList.add('kj-ready');
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();

  // Theme editor: re-run lightweight inits when sections re-render
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); measureHeader(); });
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh().catch(function () {}); });
})();
